import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, borderRadius, shadows } from '../styles/designTokens';
import { FiUser, FiMail, FiCalendar, FiChevronRight } from 'react-icons/fi';
import { Customer } from '../models/CustomerModels';
import { getRelativeTimeKST } from '../utils/dateUtils';

// 모바일 고객 목록 테이블 Props 인터페이스
interface CustomerListTableMobileProps {
  customers: Customer[] | null;
  loading: boolean;
  formatDateTime: (dateString: string) => string;
  formatCurrency: (amount: number) => string;
  formatMarketingStatus: (status: boolean) => string;
}

// 모바일 테이블 컨테이너
const MobileTableContainer = styled.div`
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  box-shadow: ${shadows.base};
  border: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 테이블 헤더
const MobileTableHeader = styled.div`
  background-color: ${colors.semantic.background.secondary};
  padding: 16px;
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 테이블 제목
const MobileTableTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0;
`;

// 모바일 고객 카드 리스트
const MobileCustomerList = styled.div`
  display: flex;
  flex-direction: column;
`;

// 모바일 테이블 행
const MobileTableRow = styled.div`
  border-bottom: 1px solid ${colors.secondary[100]};
  cursor: pointer;
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${colors.semantic.background.secondary};
  }

  &:active {
    background-color: ${colors.secondary[100]};
  }
`;

// 모바일 테이블 행 헤더 (이름과 ID)
const MobileRowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px 8px 16px;
  background-color: ${colors.semantic.background.secondary};
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 고객 기본 정보
const MobileCustomerBasicInfo = styled.div`
  flex: 1;
`;

// 모바일 고객 이름
const MobileCustomerName = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0 0 2px 0;
`;

// 모바일 고객 ID
const MobileCustomerId = styled.p`
  font-size: 12px;
  color: ${colors.semantic.text.tertiary};
  margin: 0;
`;

// 모바일 화살표 아이콘
const MobileChevronIcon = styled.div`
  color: ${colors.semantic.text.disabled};
  font-size: 16px;
`;

// 모바일 테이블 바디 (상세 정보)
const MobileTableBody = styled.div`
  padding: 12px 16px;
`;

// 모바일 정보 행
const MobileInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid ${colors.secondary[100]};
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

// 모바일 정보 라벨
const MobileInfoLabel = styled.span`
  font-size: 13px;
  color: ${colors.semantic.text.tertiary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;

  .icon {
    font-size: 14px;
    color: ${colors.semantic.text.disabled};
  }
`;

// 모바일 정보 값
const MobileInfoValue = styled.span`
  font-size: 13px;
  color: ${colors.semantic.text.secondary};
  font-weight: 500;
  text-align: right;
`;

// 모바일 마케팅 상태 배지
const MobileMarketingBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.xl};
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.color};
  background-color: ${props => props.color}20;
  border: 1px solid ${props => props.color}40;
`;

// 모바일 로딩 스피너
const MobileLoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid ${colors.semantic.border.primary};
    border-top: 3px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 모바일 빈 상태 메시지
const MobileEmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${colors.semantic.text.tertiary};

  .icon {
    font-size: 48px;
    color: ${colors.neutral[300]};
    margin-bottom: 16px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.semantic.text.secondary};
    margin: 0 0 8px 0;
  }
  
  p {
    font-size: 14px;
    margin: 0;
  }
`;

// 모바일 고객 목록 테이블 컴포넌트
const CustomerListTableMobile: React.FC<CustomerListTableMobileProps> = ({
  customers,
  loading,
  formatDateTime,
  formatCurrency,
  formatMarketingStatus
}) => {
  const navigate = useNavigate();

  // 고객 상세 페이지로 이동 (모바일 버전)
  const handleCustomerClick = (userIdx: number) => {
    navigate(`/mobile/customers/${userIdx}`);
  };

  return (
    <MobileTableContainer>
      <MobileTableHeader>
        <MobileTableTitle>고객 목록</MobileTableTitle>
      </MobileTableHeader>

      {/* 로딩 상태 */}
      {loading && (
        <MobileLoadingSpinner>
          <div className="spinner" />
        </MobileLoadingSpinner>
      )}

      {/* 빈 상태 */}
      {!loading && (!customers || customers.length === 0) && (
        <MobileEmptyState>
          <FiUser className="icon" />
          <h3>고객이 없습니다</h3>
          <p>검색 조건을 변경하거나 새로고침해보세요.</p>
        </MobileEmptyState>
      )}

      {/* 고객 목록 */}
      {!loading && customers && customers.length > 0 && (
        <MobileCustomerList>
          {customers.map((customer) => {
            const marketingStatusText = formatMarketingStatus(customer.marketing_check);
            const marketingColor = customer.marketing_check
              ? colors.success[600]
              : colors.error[600];

            return (
              <MobileTableRow
                key={customer.user_idx}
                onClick={() => handleCustomerClick(customer.user_idx)}
              >
                {/* 테이블 행 헤더 (이름과 ID) */}
                <MobileRowHeader>
                  <MobileCustomerBasicInfo>
                    <MobileCustomerName>{customer.user_name}</MobileCustomerName>
                    <MobileCustomerId>idx: {customer.user_idx} | ID: {customer.user_id}</MobileCustomerId>
                  </MobileCustomerBasicInfo>
                  <MobileChevronIcon>
                    <FiChevronRight />
                  </MobileChevronIcon>
                </MobileRowHeader>

                {/* 테이블 바디 (상세 정보) */}
                <MobileTableBody>
                  <MobileInfoRow>
                    <MobileInfoLabel>
                      <FiMail className="icon" />
                      이메일
                    </MobileInfoLabel>
                    <MobileInfoValue>{customer.user_email}</MobileInfoValue>
                  </MobileInfoRow>

                  <MobileInfoRow>
                    <MobileInfoLabel>
                      <FiCalendar className="icon" />
                      마지막 로그인
                    </MobileInfoLabel>
                    <MobileInfoValue>{getRelativeTimeKST(customer.last_datetime)}</MobileInfoValue>
                  </MobileInfoRow>

                  <MobileInfoRow>
                    <MobileInfoLabel>결제금액</MobileInfoLabel>
                    <MobileInfoValue>{formatCurrency(customer.user_paid)}</MobileInfoValue>
                  </MobileInfoRow>

                  <MobileInfoRow>
                    <MobileInfoLabel>전체 키워드</MobileInfoLabel>
                    <MobileInfoValue>{customer.total_keywords.toLocaleString('ko-KR')}</MobileInfoValue>
                  </MobileInfoRow>

                  <MobileInfoRow>
                    <MobileInfoLabel>활성 키워드</MobileInfoLabel>
                    <MobileInfoValue>{customer.active_keywords.toLocaleString('ko-KR')}</MobileInfoValue>
                  </MobileInfoRow>

                  <MobileInfoRow>
                    <MobileInfoLabel>마케팅 동의</MobileInfoLabel>
                    <MobileInfoValue>
                      <MobileMarketingBadge color={marketingColor}>
                        {marketingStatusText}
                      </MobileMarketingBadge>
                    </MobileInfoValue>
                  </MobileInfoRow>
                </MobileTableBody>
              </MobileTableRow>
            );
          })}
        </MobileCustomerList>
      )}
    </MobileTableContainer>
  );
};

export default CustomerListTableMobile; 